import React, { useEffect, useMemo, useState } from "react";
import { issuesList } from "../../api/Issues";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Nav from "../master/NavBar";
import Header from "../master/SideBar";
import { Link } from "react-router-dom";
import { imgBaseUrl } from "../../api/api";
import { formatToDateTime } from "../master/FormatToDateTime";
import UserHelpLayout from "./UserHelpLayout";
import usePrivateLink from "../config/usePrivateLink";

export default function IssueList() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const hasAccess = usePrivateLink();

  const getData = async () => {
    setLoading(true);

    try {
      const response = await issuesList();

      if (response?.data?.status) {
        setData(response.data.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message, 10000);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
        10000,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  /*
   * API appears to return:
   *
   * response.data.data
   *
   * as the actual issue array.
   */
  const issues = Array.isArray(data?.data) ? data.data : [];

  const totalIssues = issues.length;

  const resolvedIssues = useMemo(
    () => issues.filter((item) => String(item.status) === "1").length,
    [issues],
  );

  const pendingIssues = totalIssues - resolvedIssues;

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <UserHelpLayout />

                  <div className="col-md-12">
                    {/* =====================================================
                        PAGE HEADER
                    ====================================================== */}
                    <div className="card mb-4">
                      <div className="card-body p-4">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                          <div>
                            <div className="d-flex align-items-center gap-3 mb-2">
                              <div className="bg-label-primary rounded-3 p-2">
                                <i className="ri-customer-service-2-line ri-24px"></i>
                              </div>

                              <div>
                                <span className="text-muted small fw-medium">
                                  SUPPORT CENTER
                                </span>

                                <h4 className="mb-0 mt-1">My Support Issues</h4>
                              </div>
                            </div>

                            <p className="text-muted mb-0">
                              Track your support requests and their resolution
                              status.
                            </p>
                          </div>
                          {hasAccess("/issue/Add") && (

                            <Link to="/user/help" className="btn btn-primary">
                              <i className="ri-add-line me-2"></i>
                              New Issue
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        SUMMARY CARDS
                    ====================================================== */}
                    <div className="row g-3 mb-4">
                      {/* TOTAL */}
                      <div className="col-sm-6 col-xl-4">
                        <div className="card h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <span className="text-muted small">
                                  Total Issues
                                </span>

                                <h3 className="mb-0 mt-2">{totalIssues}</h3>
                              </div>

                              <div className="bg-label-primary rounded-3 p-2">
                                <i className="ri-file-list-3-line ri-24px"></i>
                              </div>
                            </div>

                            <div className="mt-3">
                              <span className="text-muted small">
                                All submitted support requests
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* PENDING */}
                      <div className="col-sm-6 col-xl-4">
                        <div className="card h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <span className="text-muted small">
                                  Pending
                                </span>

                                <h3 className="mb-0 mt-2">{pendingIssues}</h3>
                              </div>

                              <div className="bg-label-warning rounded-3 p-2">
                                <i className="ri-time-line ri-24px"></i>
                              </div>
                            </div>

                            <div className="mt-3">
                              <span className="text-muted small">
                                Issues awaiting resolution
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* RESOLVED */}
                      <div className="col-sm-6 col-xl-4">
                        <div className="card h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <span className="text-muted small">
                                  Resolved
                                </span>

                                <h3 className="mb-0 mt-2">{resolvedIssues}</h3>
                              </div>

                              <div className="bg-label-success rounded-3 p-2">
                                <i className="ri-checkbox-circle-line ri-24px"></i>
                              </div>
                            </div>

                            <div className="mt-3">
                              <span className="text-muted small">
                                Successfully resolved issues
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        ISSUE LIST
                    ====================================================== */}
                    <div className="card">
                      <div className="card-body p-0">
                        {/* TABLE HEADER */}
                        <div className="p-4 border-bottom">
                          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                            <div>
                              <h5 className="mb-1">
                                <i className="ri-list-check-2 me-2 text-primary"></i>
                                Issue History
                              </h5>

                              <p className="text-muted small mb-0">
                                View and track all your submitted support
                                requests.
                              </p>
                            </div>

                            <span className="badge bg-label-primary">
                              {totalIssues}{" "}
                              {totalIssues === 1 ? "Issue" : "Issues"}
                            </span>
                          </div>
                        </div>

                        {/* =================================================
                            EMPTY STATE
                        ================================================== */}
                        {issues.length === 0 ? (
                          <div className="text-center py-5 px-3">
                            <div
                              className="bg-label-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                              style={{
                                width: "72px",
                                height: "72px",
                              }}
                            >
                              <i className="ri-inbox-line ri-32px"></i>
                            </div>

                            <h5 className="mb-2">No support issues found</h5>

                            <p
                              className="text-muted mx-auto mb-4"
                              style={{ maxWidth: "450px" }}
                            >
                              You haven't submitted any support requests yet.
                              Create a new issue if you need assistance.
                            </p>
 {hasAccess("/issue/Add") && (
                            <Link to="/user/help" className="btn btn-primary">
                              <i className="ri-add-line me-2"></i>
                              Create New Issue
                            </Link>
 )}
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table align-middle mb-0">
                              <thead>
                                <tr>
                                  <th className="ps-4">#</th>
                                  <th>Ticket</th>
                                  <th>Issue</th>
                                  <th>Message</th>
                                  <th>Attachment</th>
                                  <th>Requested</th>
                                  <th>Status</th>
                                  <th>Resolved</th>
                                </tr>
                              </thead>

                              <tbody>
                                {issues.map((item, i) => {
                                  const isResolved =
                                    String(item.status) === "1";

                                  return (
                                    <tr key={item.id ?? i}>
                                      {/* INDEX */}
                                      <td className="ps-4">
                                        <span className="text-muted fw-medium">
                                          {String(i + 1).padStart(2, "0")}
                                        </span>
                                      </td>

                                      {/* TOKEN */}
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <div className="bg-label-primary rounded-2 p-2">
                                            <i className="ri-ticket-2-line"></i>
                                          </div>

                                          <div>
                                            <span className="small text-muted d-block">
                                              Token ID
                                            </span>

                                            <span className="fw-semibold">
                                              {item.token || "—"}
                                            </span>
                                          </div>
                                        </div>
                                      </td>

                                      {/* SUBJECT */}
                                      <td>
                                        <div
                                          className="fw-semibold"
                                          title={item.subject}
                                          style={{
                                            minWidth: "160px",
                                            maxWidth: "230px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {item.subject || "No subject"}
                                        </div>
                                      </td>

                                      {/* MESSAGE */}
                                      <td>
                                        <div
                                          className="text-muted"
                                          title={item.message}
                                          style={{
                                            minWidth: "200px",
                                            maxWidth: "300px",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                          }}
                                        >
                                          {item.message || "No message"}
                                        </div>
                                      </td>

                                      {/* IMAGE */}
                                      <td>
                                        {item.img ? (
                                          <a
                                            href={imgBaseUrl + item.img}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="d-inline-block"
                                            title="View attachment"
                                          >
                                            <div
                                              className="border rounded-3 overflow-hidden d-flex align-items-center justify-content-center"
                                              style={{
                                                width: "54px",
                                                height: "54px",
                                              }}
                                            >
                                              <img
                                                src={imgBaseUrl + item.img}
                                                alt="Issue attachment"
                                                style={{
                                                  width: "100%",
                                                  height: "100%",
                                                  objectFit: "cover",
                                                }}
                                              />
                                            </div>
                                          </a>
                                        ) : (
                                          <span className="text-muted small">
                                            <i className="ri-image-line me-1"></i>
                                            No attachment
                                          </span>
                                        )}
                                      </td>

                                      {/* REQUEST DATE */}
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <i className="ri-calendar-event-line text-muted"></i>

                                          <span className="small">
                                            {item.request_datetime
                                              ? formatToDateTime(
                                                item.request_datetime,
                                              )
                                              : "—"}
                                          </span>
                                        </div>
                                      </td>

                                      {/* STATUS */}
                                      <td>
                                        {isResolved ? (
                                          <span className="badge bg-label-success px-3 py-2">
                                            <i className="ri-checkbox-circle-fill me-1"></i>
                                            Resolved
                                          </span>
                                        ) : (
                                          <span className="badge bg-label-warning px-3 py-2">
                                            <i className="ri-time-fill me-1"></i>
                                            Pending
                                          </span>
                                        )}
                                      </td>

                                      {/* RESOLVED DATE */}
                                      <td>
                                        {item.resolved_datetime ? (
                                          <div className="d-flex align-items-center gap-2">
                                            <i className="ri-check-double-line text-success"></i>

                                            <span className="small">
                                              {formatToDateTime(
                                                item.resolved_datetime,
                                              )}
                                            </span>
                                          </div>
                                        ) : (
                                          <span className="text-muted small">
                                            —
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
